import React, { useEffect, useState } from 'react';
import { TFunction } from 'i18next';
import { IconButton, Stack } from '@mui/material';
import { trackMixpanel } from '@monorepo/client-common';
import { SystemIcon } from '@monorepo/nova-icons';
import { EventTypes } from '@monorepo/types';
import { getScoreLevel } from './trade-score-card.constants';
import {
  ButtonText,
  CloseButtonWrapper,
  ContentStack,
  DescriptionList,
  DescriptionListItem,
  InfoDescription,
  InfoStack,
  InfoTitle,
  MetricBox,
  MetricBoxMiddle,
  MetricLabel,
  MetricsContainer,
  MetricValue,
  ScoreIndicatorWrapper,
  ScoreLabel,
  ScoreMetricsStack,
  ScoreNumber,
  ScoreTextStack,
  SpeechBubblePointer,
  SpeechBubblePointerInner,
  StyledCard,
  TitleText,
  ToggleButton,
  TradeScoreCardContainer,
} from './trade-score-card.styles';

export interface TradeScoreCardProps {
  score: number;
  metrics: {
    risk: number;
    precision: number;
    payoff: number;
  };
  onButtonClick?: () => void;
  onPublishClick?: (data: {
    score: number;
    metrics: { risk: number; precision: number; payoff: number };
    metadata?: Record<string, unknown>;
  }) => void;
  metadata?: Record<string, unknown>;
  onClose?: () => void;
  variant?: 'desktop' | 'mobile';
  t?: TFunction<any> | ((key: string) => string);
  userId?: string;
  tradeId?: string | number;
  isUnsupportedAsset?: boolean;
}

const getProgressPercentage = (score: number): number => {
  return (score / 100) * 100;
};

const ScoreIndicator: React.FC<{ score: number; size?: number }> = ({
  score,
  size = 102,
}) => {
  const percentage = getProgressPercentage(score);
  const label = getScoreLevel(score);
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  const [animatedScore, setAnimatedScore] = useState(0);
  const circumference = 2 * Math.PI * 48; // r = 48
  const strokeDasharray = circumference;
  const strokeDashoffset =
    circumference - (animatedPercentage / 100) * circumference;

  useEffect(() => {
    // Animate from 0 to target percentage on mount
    const duration = 1500; // 1.5 seconds
    const startTime = Date.now();
    const startValue = 0;
    const endValue = percentage;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out easing function
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (endValue - startValue) * easedProgress;

      setAnimatedPercentage(currentValue);

      // Animate score from 0 to target score
      const currentScore = Math.round(
        startValue + (score - startValue) * easedProgress,
      );
      setAnimatedScore(currentScore);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Ensure final values are set
        setAnimatedPercentage(endValue);
        setAnimatedScore(score);
      }
    };

    requestAnimationFrame(animate);
  }, [percentage, score]);

  return (
    <ScoreIndicatorWrapper>
      <svg
        width={size}
        height={size}
        viewBox="0 0 102 102"
        style={{ transform: 'rotate(-90deg)' }}
      >
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#8458FC" />
            <stop offset="50%" stopColor="#E94C89" />
            <stop offset="100%" stopColor="#FF7A3D" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <circle
          cx="51"
          cy="51"
          r="40"
          fill="none"
          stroke="#BBBDBF"
          strokeWidth="2"
        />
        <circle
          cx="51"
          cy="51"
          r="48"
          fill="none"
          stroke="url(#gradient)"
          strokeWidth="4"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          filter="url(#glow)"
        />
      </svg>
      <ScoreTextStack alignItems="center" justifyContent="center">
        <ScoreNumber>{animatedScore}</ScoreNumber>
        <ScoreLabel>{label}</ScoreLabel>
      </ScoreTextStack>
    </ScoreIndicatorWrapper>
  );
};

const getScoreTitle = (score: number, t?: (key: string) => string): string => {
  const translate =
    t ||
    ((key: string) => {
      const defaults: Record<string, string> = {
        'tradeScore.title.elite': 'Elite Execution',
        'tradeScore.title.good': 'Good Execution',
        'tradeScore.title.fair': 'Fair Execution',
        'tradeScore.title.average': 'Average Execution',
        'tradeScore.title.poor': 'Poor Execution',
      };
      return defaults[key] || key;
    });

  if (score >= 80) return translate('tradeScore.title.elite');
  if (score >= 60) return translate('tradeScore.title.good');
  if (score >= 40) return translate('tradeScore.title.fair');
  if (score >= 20) return translate('tradeScore.title.average');
  return translate('tradeScore.title.poor');
};

const getParameterSummary = (
  parameterScore: number | undefined | null,
  parameterName: 'risk' | 'precision' | 'payoff',
  t?: (key: string) => string,
): string => {
  const translate =
    t ||
    ((key: string) => {
      const defaults: Record<string, string> = {
        'tradeScore.risk': 'Risk',
        'tradeScore.precision': 'Precision',
        'tradeScore.payoff': 'Payoff',
        'tradeScore.summary.unavailable': 'Summary unavailable for this trade.',
        'tradeScore.summary.risk.elite':
          'Risk is well controlled, with position size and exposure aligned to the setup.',
        'tradeScore.summary.risk.good':
          'Risk is managed correctly, with minor room for improvement.',
        'tradeScore.summary.risk.fair':
          'Risk is acceptable, but sizing or protection could be more precise.',
        'tradeScore.summary.risk.average':
          'Risk exposure is higher than optimal for this setup.',
        'tradeScore.summary.risk.poor':
          'Risk is poorly managed and outweighs the potential reward.',
        'tradeScore.summary.precision.elite':
          'Entry and exit are well timed and aligned with the market structure.',
        'tradeScore.summary.precision.good':
          'Execution is solid, with small deviations from optimal timing.',
        'tradeScore.summary.precision.fair':
          'Timing lacks consistency and reduces execution quality.',
        'tradeScore.summary.precision.average':
          'Entry or exit timing is imprecise for this market move.',
        'tradeScore.summary.precision.poor':
          'Execution timing is misaligned with the market structure.',
        'tradeScore.summary.payoff.elite':
          'Reward-to-risk is strong and supports long-term profitability.',
        'tradeScore.summary.payoff.good':
          'Payoff is positive, but the reward-to-risk could be improved.',
        'tradeScore.summary.payoff.fair':
          'Payoff is limited relative to the risk taken.',
        'tradeScore.summary.payoff.average':
          'The trade closes with a weak reward-to-risk outcome.',
        'tradeScore.summary.payoff.poor':
          'The payoff does not justify the risk taken in this trade.',
      };
      return defaults[key] || key;
    });

  const parameterLabel = translate(`tradeScore.${parameterName}`);

  if (
    parameterScore === null ||
    parameterScore === undefined ||
    isNaN(parameterScore)
  ) {
    const unavailableText = translate('tradeScore.summary.unavailable');
    return `${parameterLabel} - ${unavailableText}`;
  }

  const score = Number(parameterScore);
  const level =
    score >= 80
      ? 'elite'
      : score >= 60
        ? 'good'
        : score >= 40
          ? 'fair'
          : score >= 20
            ? 'average'
            : 'poor';

  const explanation = translate(`tradeScore.summary.${parameterName}.${level}`);
  return `${parameterLabel} - ${explanation}`;
};

const getScoreBulletPoints = (
  metrics: {
    risk: number;
    precision: number;
    payoff: number;
  },
  t?: (key: string) => string,
): string[] => {
  return [
    getParameterSummary(metrics.risk, 'risk', t),
    getParameterSummary(metrics.precision, 'precision', t),
    getParameterSummary(metrics.payoff, 'payoff', t),
  ];
};

const getCtaText = (score: number, t?: (key: string) => string): string => {
  const translate =
    t ||
    ((key: string) => {
      const defaults: Record<string, string> = {
        'tradeScore.cta.talkTheTrade': 'Talk The Trade',
        'tradeScore.cta.publishYourTradeScore': 'Publish Your Trade Score',
      };
      return defaults[key] || key;
    });

  if (score >= 80) {
    return translate('tradeScore.cta.talkTheTrade');
  }
  return translate('tradeScore.cta.publishYourTradeScore');
};

const defaultTranslations: Record<string, string> = {
  'tradeScore.risk': 'Risk',
  'tradeScore.precision': 'Precision',
  'tradeScore.payoff': 'Payoff',
  'tradeScore.infoTitle': 'How is the Trade Score Calculated?',
  'tradeScore.infoDescription':
    'Our proprietary Trade Score model analyses thousands of metrics and data points across three core pillars: Risk, Discipline, Precision and Payoff Quality. This provides a holistic, objective measure of execution quality for every trade.',
  'tradeScore.backButton': 'Back to Score',
  'tradeScore.learnMoreButton': 'What is Trade Score?',
  'tradeScore.cta.talkTheTrade': 'Talk The Trade',
  'tradeScore.cta.publishYourTradeScore': 'Publish Your Trade Score',
  'tradeScore.title.elite': 'Elite Execution',
  'tradeScore.title.good': 'Good Execution',
  'tradeScore.title.fair': 'Fair Execution',
  'tradeScore.title.average': 'Average Execution',
  'tradeScore.title.poor': 'Poor Execution',
  'tradeScore.summary.unavailable': 'Summary unavailable for this trade.',
  'tradeScore.summary.risk.elite':
    'Risk is well controlled, with position size and exposure aligned to the setup.',
  'tradeScore.summary.risk.good':
    'Risk is managed correctly, with minor room for improvement.',
  'tradeScore.summary.risk.fair':
    'Risk is acceptable, but sizing or protection could be more precise.',
  'tradeScore.summary.risk.average':
    'Risk exposure is higher than optimal for this setup.',
  'tradeScore.summary.risk.poor':
    'Risk is poorly managed and outweighs the potential reward.',
  'tradeScore.summary.precision.elite':
    'Entry and exit are well timed and aligned with the market structure.',
  'tradeScore.summary.precision.good':
    'Execution is solid, with small deviations from optimal timing.',
  'tradeScore.summary.precision.fair':
    'Timing lacks consistency and reduces execution quality.',
  'tradeScore.summary.precision.average':
    'Entry or exit timing is imprecise for this market move.',
  'tradeScore.summary.precision.poor':
    'Execution timing is misaligned with the market structure.',
  'tradeScore.summary.payoff.elite':
    'Reward-to-risk is strong and supports long-term profitability.',
  'tradeScore.summary.payoff.good':
    'Payoff is positive, but the reward-to-risk could be improved.',
  'tradeScore.summary.payoff.fair':
    'Payoff is limited relative to the risk taken.',
  'tradeScore.summary.payoff.average':
    'The trade closes with a weak reward-to-risk outcome.',
  'tradeScore.summary.payoff.poor':
    'The payoff does not justify the risk taken in this trade.',
  'tradeScore.unsupported.title': 'Scoring Unavailable',
  'tradeScore.unsupported.body':
    'Trade scores are currently limited to Forex. Crypto support is coming soon! You can still document your strategy by creating a post about this asset.',
  'tradeScore.unsupported.cta': 'Talk The Trade',
};

export const TradeScoreCard: React.FC<TradeScoreCardProps> = ({
  score,
  metrics,
  onButtonClick,
  onPublishClick,
  metadata,
  onClose,
  variant = 'desktop',
  t,
  userId,
  tradeId,
  isUnsupportedAsset = false,
}) => {
  const [showInfo, setShowInfo] = useState(false);
  const isMobile = variant === 'mobile';

  const translate = (key: string): string => {
    if (!t) {
      return defaultTranslations[key] || key;
    }

    try {
      // Handle both TFunction and simple function types
      const translated =
        typeof t === 'function' && t.length === 1
          ? (t as (key: string) => string)(key)
          : (t as TFunction<any>)(key, { defaultValue: key });

      // If translation function returns the key itself (meaning translation not found),
      // fall back to default translations
      if (translated === key && defaultTranslations[key]) {
        return defaultTranslations[key];
      }

      return typeof translated === 'string' ? translated : key;
    } catch {
      return defaultTranslations[key] || key;
    }
  };

  const title = getScoreTitle(score, translate);
  const bulletPoints = getScoreBulletPoints(metrics, translate);
  const ctaText = getCtaText(score, translate);

  const handleUnsupportedCtaClick = () => {
    onPublishClick?.({
      score: 0,
      metrics: { risk: 0, precision: 0, payoff: 0 },
      metadata,
    });
  };

  // Get tradeId from props or metadata
  const resolvedTradeId =
    tradeId || (metadata?.['tradeId'] as string | number | undefined);

  const trackTradeScoreEvent = (
    eventType: EventTypes,
    extraProperties?: Record<string, unknown>,
  ) => {
    // Guard against empty userId
    if (!userId) {
      return;
    }

    const baseProperties = {
      trade_id: resolvedTradeId,
      user_id: userId,
      trade_score: score,
      risk_score: metrics.risk,
      precision_score: metrics.precision,
      payoff_score: metrics.payoff,
    };

    trackMixpanel({
      event: eventType,
      properties: {
        ...baseProperties,
        ...extraProperties,
      },
    });
  };

  const handleButtonClick = () => {
    setShowInfo(!showInfo);
    onButtonClick?.();
  };

  const handleInfoIconClick = () => {
    trackTradeScoreEvent(EventTypes.TRADE_SCORE_TOOLTIP_CLICKED);
    setShowInfo(!showInfo);
  };

  const handleCtaClick = () => {
    // Determine CTA type based on score: score >= 80 = 'talk_the_trade', else = 'publish_trade_score'
    const ctaType = score >= 80 ? 'talk_the_trade' : 'publish_trade_score';

    trackTradeScoreEvent(EventTypes.TRADE_SCORE_CTA_CLICKED, {
      cta_type: ctaType,
    });

    onPublishClick?.({
      score,
      metrics,
      metadata,
    });
  };

  return (
    <TradeScoreCardContainer isMobile={isMobile}>
      <StyledCard isMobile={isMobile}>
        {isMobile && onClose && (
          <CloseButtonWrapper>
            <SystemIcon
              iconName="X"
              size={16}
              onClick={onClose}
              sx={(theme) => ({
                cursor: 'pointer',
                color: theme.palette.secondaryInvert.main,
              })}
            />
          </CloseButtonWrapper>
        )}
        {isUnsupportedAsset ? (
          <ContentStack
            spacing={isMobile ? 6 : 3}
            alignItems="flex-start"
            isMobile={isMobile}
          >
            <TitleText
              sx={{ fontSize: (theme) => theme.typography.pxToRem(20) }}
            >
              {translate('tradeScore.unsupported.title')}
            </TitleText>
            <InfoDescription sx={{ textAlign: 'left' }}>
              {translate('tradeScore.unsupported.body')}
            </InfoDescription>
            <ToggleButton
              onClick={handleUnsupportedCtaClick}
              isMobile={isMobile}
            >
              <ButtonText>{translate('tradeScore.unsupported.cta')}</ButtonText>
              <SystemIcon iconName="CaretRight" size={16} color="inherit" />
            </ToggleButton>
          </ContentStack>
        ) : showInfo ? (
          <InfoStack spacing={isMobile ? 0 : 3} isMobile={isMobile}>
            <Stack spacing={3}>
              <InfoTitle>{translate('tradeScore.infoTitle')}</InfoTitle>
              <InfoDescription>
                {translate('tradeScore.infoDescription')}
              </InfoDescription>
            </Stack>
            <ToggleButton onClick={handleButtonClick} isMobile={isMobile}>
              <SystemIcon iconName="CaretLeft" size={16} color="inherit" />
              <ButtonText>{translate('tradeScore.backButton')}</ButtonText>
            </ToggleButton>
          </InfoStack>
        ) : (
          <ContentStack
            spacing={isMobile ? 6 : 3}
            alignItems={isMobile ? 'center' : 'flex-start'}
            isMobile={isMobile}
          >
            <ScoreMetricsStack
              direction={isMobile ? 'column' : 'row'}
              alignItems="center"
              isMobile={isMobile}
            >
              <ScoreIndicator score={score} />

              <MetricsContainer>
                <Stack direction="row">
                  <MetricBox isMobile={isMobile}>
                    <MetricLabel>{translate('tradeScore.risk')}</MetricLabel>
                    <MetricValue>{metrics.risk}</MetricValue>
                  </MetricBox>

                  <MetricBoxMiddle isMobile={isMobile}>
                    <MetricLabel>
                      {translate('tradeScore.precision')}
                    </MetricLabel>
                    <MetricValue>{metrics.precision}</MetricValue>
                  </MetricBoxMiddle>

                  <MetricBox isMobile={isMobile}>
                    <MetricLabel>{translate('tradeScore.payoff')}</MetricLabel>
                    <MetricValue>{metrics.payoff}</MetricValue>
                  </MetricBox>
                </Stack>
              </MetricsContainer>
            </ScoreMetricsStack>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <TitleText>{title}</TitleText>
              <IconButton onClick={handleInfoIconClick}>
                <SystemIcon iconName="Info" size={24} />
              </IconButton>
            </Stack>

            <DescriptionList isMobile={isMobile}>
              {bulletPoints.map((bullet, index) => (
                <DescriptionListItem key={index}>{bullet}</DescriptionListItem>
              ))}
            </DescriptionList>

            <ToggleButton onClick={handleCtaClick} isMobile={isMobile}>
              <ButtonText>{ctaText}</ButtonText>
              <SystemIcon iconName="CaretRight" size={16} color="inherit" />
            </ToggleButton>
          </ContentStack>
        )}
      </StyledCard>

      {!isMobile && (
        <SpeechBubblePointer>
          <SpeechBubblePointerInner />
        </SpeechBubblePointer>
      )}
    </TradeScoreCardContainer>
  );
};
